/**
 * PlanValidator — EX-2 : Validation humaine obligatoire du plan.
 * Reçoit le plan via WebSocket, permet un éditeur d'arborescence complet
 * (renommer, promouvoir/rétrograder, réordonner/déplacer par drag & drop,
 * fusionner, supprimer, ajouter) avant d'approuver et de lancer le chunking.
 */
import React, { useEffect, useRef, useState } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { useDocumentStore } from "../../store";

const WS_BASE = process.env.NODE_ENV === "production"
  ? `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}`
  : (process.env.REACT_APP_WS_URL || "ws://localhost:8000");
let _tmpIdCounter = 0;
const _tmpId = () => `tmp-${Date.now()}-${_tmpIdCounter++}`;

export default function PlanValidator({ doc, onApproved, onDismiss }) {
  const [plan, setPlan] = useState(doc.validated_plan || []);
  const [wsStatus, setWsStatus] = useState("connecting");
  const [activeDragId, setActiveDragId] = useState(null);
  const [errors, setErrors] = useState([]);
  const wsRef = useRef(null);
  const { approvePlan } = useDocumentStore();

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  useEffect(() => {
    if (!doc?.doc_id) return;
    const token = localStorage.getItem("access_token");
    const ws = new WebSocket(`${WS_BASE}/ws/documents/${doc.doc_id}/plan/?token=${token}`);
    wsRef.current = ws;

    ws.onopen = () => setWsStatus("connected");
    ws.onclose = () => setWsStatus("closed");
    ws.onerror = () => setWsStatus("error");

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "plan_ready") {
          setPlan(data.plan);
        } else if (data.type === "ingestion_progress") {
          if (data.status === "done") onApproved?.();
        }
      } catch {}
    };

    return () => ws.close();
  }, [doc.doc_id]);

  const handleRename = (path, newTitle) => {
    setPlan((prev) => _renamePlan(prev, path, newTitle));
  };

  const handlePromote = (path) => {
    setPlan((prev) => _changeLevel(prev, path, -1));
  };

  const handleDemote = (path) => {
    setPlan((prev) => _changeLevel(prev, path, +1));
  };

  const handleDelete = (path) => {
    const node = _getNode(plan, path);
    const childCount = _countDescendants(node);
    if (childCount > 0) {
      const ok = window.confirm(
        `Supprimer aussi les ${childCount} sous-section(s) de « ${node.title} » ?`
      );
      if (!ok) return;
    }
    setPlan((prev) => _deleteNode(prev, path));
  };

  const handleMergeWithNext = (path) => {
    const siblings = _getSiblings(plan, path);
    const index = path[path.length - 1];
    if (index >= siblings.length - 1) return; // pas de section suivante au même niveau
    const target = siblings[index];
    const source = siblings[index + 1];
    if (source.children?.length) {
      const ok = window.confirm(
        `Les sous-sections de « ${source.title} » seront rattachées à « ${target.title} ». Continuer ?`
      );
      if (!ok) return;
    }
    setPlan((prev) => _mergeNodes(prev, path));
  };

  const handleAddSibling = (path, title) => {
    const parentLevel = path.length > 1 ? _getNode(plan, path.slice(0, -1)).level : 0;
    const siblingLevel = _getNode(plan, path).level;
    setPlan((prev) => _insertAfter(prev, path, {
      node_id: null,
      origin: "new",
      anchor_line: null,
      level: siblingLevel || parentLevel + 1,
      title,
      body_text: "",
      children: [],
    }));
  };

  const handleDragStart = (event) => {
    setActiveDragId(event.active.id);
  };

  const handleDragEnd = (event) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const fromPath = active.data.current?.path;
    const dropZone = over.data.current;
    if (!fromPath || !dropZone) return;

    setPlan((prev) => {
      const moved = _moveNode(prev, fromPath, dropZone.path, dropZone.mode);
      return moved;
    });
  };

  const handleApprove = async () => {
    setErrors([]);
    try {
      await approvePlan(doc.id, plan);
      onApproved?.();
    } catch (e) {
      const serverErrors = e?.response?.data?.errors;
      if (serverErrors?.length) {
        setErrors(serverErrors);
      } else {
        alert("Erreur lors de l'approbation : " + e.message);
      }
    }
  };

  return (
    <div className="plan-validator">
      <div className="plan-validator__header">
        <h3>📋 Validation du plan — {doc.title}</h3>
        <span className={`ws-status ws-status--${wsStatus}`}>{wsStatus}</span>
      </div>

      <p className="plan-validator__info">
        Vérifiez et modifiez l'arborescence des sections avant de lancer le chunking : renommez,
        promouvez/rétrogradez (◀︎▶︎), glissez-déposez pour réordonner ou changer de section parente,
        fusionnez (🔗) ou supprimez (🗑️) une section, ajoutez-en une nouvelle (➕).
        <br />
        <strong>Cette étape est obligatoire</strong> pour garantir une segmentation correcte.
      </p>

      {errors.length > 0 && (
        <div className="plan-validator__errors">
          {errors.map((err, i) => (
            <div key={i} className="plan-validator__error">⚠️ {err}</div>
          ))}
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="plan-tree">
          <PlanTree
            nodes={plan}
            onRename={handleRename}
            onPromote={handlePromote}
            onDemote={handleDemote}
            onDelete={handleDelete}
            onMergeWithNext={handleMergeWithNext}
            onAddSibling={handleAddSibling}
          />
          {plan.length === 0 && (
            <AddRootButton onAdd={(title) => setPlan((prev) => [...prev, {
              node_id: null, origin: "new", anchor_line: null, level: 1, title, body_text: "", children: [],
            }])} />
          )}
        </div>
        <DragOverlay>
          {activeDragId ? <div className="plan-node plan-node--dragging">⠿ déplacement…</div> : null}
        </DragOverlay>
      </DndContext>

      <div className="plan-validator__actions">
        <button className="btn btn--primary" onClick={handleApprove}>
          ✅ Approuver le plan et lancer le chunking
        </button>
        <button className="btn btn--secondary" onClick={onDismiss}>
          Annuler
        </button>
      </div>
    </div>
  );
}

function PlanTree({ nodes, path = [], depth = 0, ...handlers }) {
  if (!nodes?.length) return null;
  return (
    <ul className={`plan-tree__level plan-tree__level--${depth}`}>
      {nodes.map((node, i) => {
        const currentPath = [...path, i];
        const key = node.node_id || `${currentPath.join(".")}`;
        return (
          <li key={key} className="plan-tree__node">
            <DropZone id={`before-${key}`} path={currentPath} mode="before" />
            <PlanNode node={node} path={currentPath} {...handlers} />
            {node.children?.length > 0 && (
              <PlanTree nodes={node.children} path={currentPath} depth={depth + 1} {...handlers} />
            )}
            {i === nodes.length - 1 && (
              <DropZone id={`after-${key}`} path={currentPath} mode="after" />
            )}
          </li>
        );
      })}
    </ul>
  );
}

function DropZone({ id, path, mode }) {
  const { setNodeRef, isOver } = useDroppable({ id, data: { path, mode } });
  return (
    <div
      ref={setNodeRef}
      className={"plan-tree__drop-line" + (isOver ? " plan-tree__drop-line--active" : "")}
    />
  );
}

function PlanNode({ node, path, onRename, onPromote, onDemote, onDelete, onMergeWithNext, onAddSibling }) {
  const [editing, setEditing] = useState(false);
  const [addingSibling, setAddingSibling] = useState(false);
  const [value, setValue] = useState(node.title);
  const [newTitle, setNewTitle] = useState("");
  const prefix = "#".repeat(node.level || 1);
  const dragId = node.node_id || `path-${path.join(".")}`;

  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
    id: dragId,
    data: { path },
  });
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `into-${dragId}`,
    data: { path, mode: "into" },
  });

  const save = () => {
    if (value.trim()) onRename(path, value.trim());
    setEditing(false);
  };

  const saveNewSibling = () => {
    if (newTitle.trim()) onAddSibling(path, newTitle.trim());
    setNewTitle("");
    setAddingSibling(false);
  };

  return (
    <div ref={setDropRef} className={"plan-node-wrapper" + (isOver ? " plan-node-wrapper--drop-target" : "")}>
      <div
        ref={setDragRef}
        className={"plan-node" + (isDragging ? " plan-node--dragging" : "")}
      >
        <span className="plan-node__handle" title="Glisser pour déplacer" {...attributes} {...listeners}>⠿</span>
        <span className="plan-node__level">{prefix}</span>
        {editing ? (
          <input
            className="plan-node__input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={save}
            onKeyDown={(e) => e.key === "Enter" && save()}
            autoFocus
          />
        ) : (
          <span className="plan-node__title" onDoubleClick={() => setEditing(true)}>
            {node.title}
          </span>
        )}
        <button className="plan-node__action" onClick={() => setEditing(true)} title="Renommer">✏️</button>
        <button
          className="plan-node__action"
          onClick={() => onPromote(path)}
          disabled={node.level <= 1}
          title="Promouvoir (niveau -1)"
        >◀︎</button>
        <button
          className="plan-node__action"
          onClick={() => onDemote(path)}
          disabled={node.level >= 6}
          title="Rétrograder (niveau +1)"
        >▶︎</button>
        <button className="plan-node__action" onClick={() => onMergeWithNext(path)} title="Fusionner avec la section suivante">🔗</button>
        <button className="plan-node__action" onClick={() => setAddingSibling(true)} title="Ajouter une section après">➕</button>
        <button className="plan-node__action plan-node__action--danger" onClick={() => onDelete(path)} title="Supprimer">🗑️</button>
      </div>
      {addingSibling && (
        <div className="plan-tree__add-row">
          <input
            className="plan-node__input"
            placeholder="Titre de la nouvelle section"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onBlur={saveNewSibling}
            onKeyDown={(e) => e.key === "Enter" && saveNewSibling()}
            autoFocus
          />
        </div>
      )}
    </div>
  );
}

function AddRootButton({ onAdd }) {
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState("");

  if (!adding) {
    return (
      <button className="plan-node__action" onClick={() => setAdding(true)} title="Ajouter une section">
        ➕ Ajouter une section
      </button>
    );
  }

  const save = () => {
    if (title.trim()) onAdd(title.trim());
    setTitle("");
    setAdding(false);
  };

  return (
    <div className="plan-tree__add-row">
      <input
        className="plan-node__input"
        placeholder="Titre de la section"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => e.key === "Enter" && save()}
        autoFocus
      />
    </div>
  );
}

// ─── Manipulation pure de l'arbre (path = liste d'indices depuis la racine) ───

function _getNode(nodes, path) {
  let level = nodes;
  let node = null;
  for (const i of path) {
    node = level[i];
    level = node?.children || [];
  }
  return node;
}

function _getSiblings(nodes, path) {
  if (path.length === 1) return nodes;
  return _getNode(nodes, path.slice(0, -1)).children || [];
}

function _countDescendants(node) {
  if (!node?.children?.length) return 0;
  return node.children.reduce((acc, c) => acc + 1 + _countDescendants(c), 0);
}

function _renamePlan(nodes, path, newTitle) {
  if (!path.length) return nodes;
  return nodes.map((node, i) => {
    if (i === path[0]) {
      if (path.length === 1) {
        return { ...node, title: newTitle, original_title: node.original_title || node.title };
      }
      return { ...node, children: _renamePlan(node.children || [], path.slice(1), newTitle) };
    }
    return node;
  });
}

function _changeLevel(nodes, path, delta) {
  if (!path.length) return nodes;
  return nodes.map((node, i) => {
    if (i === path[0]) {
      if (path.length === 1) {
        const level = Math.max(1, Math.min(6, (node.level || 1) + delta));
        return { ...node, level };
      }
      return { ...node, children: _changeLevel(node.children || [], path.slice(1), delta) };
    }
    return node;
  });
}

function _deleteNode(nodes, path) {
  if (path.length === 1) {
    return nodes.filter((_, i) => i !== path[0]);
  }
  return nodes.map((node, i) => {
    if (i === path[0]) {
      return { ...node, children: _deleteNode(node.children || [], path.slice(1)) };
    }
    return node;
  });
}

function _mergeNodes(nodes, path) {
  // Fusionne le nœud à `path` avec son frère suivant : le frère suivant disparaît,
  // ses enfants sont rattachés à la fin des enfants du nœud restant.
  if (path.length === 1) {
    const index = path[0];
    const target = nodes[index];
    const source = nodes[index + 1];
    if (!source) return nodes;
    const merged = {
      ...target,
      merged_from: [
        ...(target.merged_from || [target.node_id]).filter(Boolean),
        ...(source.merged_from || [source.node_id]).filter(Boolean),
      ],
      children: [...(target.children || []), ...(source.children || [])],
    };
    const next = [...nodes];
    next.splice(index, 2, merged);
    return next;
  }
  return nodes.map((node, i) => {
    if (i === path[0]) {
      return { ...node, children: _mergeNodes(node.children || [], path.slice(1)) };
    }
    return node;
  });
}

function _insertAfter(nodes, path, newNode) {
  const withId = { ...newNode, node_id: newNode.node_id || _tmpId() };
  if (path.length === 1) {
    const next = [...nodes];
    next.splice(path[0] + 1, 0, withId);
    return next;
  }
  return nodes.map((node, i) => {
    if (i === path[0]) {
      return { ...node, children: _insertAfter(node.children || [], path.slice(1), withId) };
    }
    return node;
  });
}

function _removeAtPath(nodes, path) {
  if (path.length === 1) {
    const node = nodes[path[0]];
    return [nodes.filter((_, i) => i !== path[0]), node];
  }
  const [childList, removed] = _removeAtPath(nodes[path[0]].children || [], path.slice(1));
  const next = nodes.map((node, i) => (i === path[0] ? { ...node, children: childList } : node));
  return [next, removed];
}

function _insertAtPath(nodes, parentPath, index, node) {
  if (!parentPath.length) {
    const next = [...nodes];
    next.splice(index, 0, node);
    return next;
  }
  return nodes.map((n, i) => {
    if (i === parentPath[0]) {
      if (parentPath.length === 1) {
        const children = [...(n.children || [])];
        children.splice(index, 0, node);
        return { ...n, children };
      }
      return { ...n, children: _insertAtPath(n.children || [], parentPath.slice(1), index, node) };
    }
    return n;
  });
}

function _renormalizeLevels(node, newParentLevel) {
  // Décale récursivement les niveaux du sous-arbre déplacé pour rester cohérent
  // avec le nouveau parent, en préservant les écarts relatifs entre le nœud
  // déplacé et ses descendants.
  const delta = newParentLevel + 1 - node.level;
  const apply = (n, d) => ({
    ...n,
    level: Math.max(1, Math.min(6, n.level + d)),
    children: (n.children || []).map((c) => apply(c, d)),
  });
  return apply(node, delta);
}

function _moveNode(rootNodes, fromPath, toPath, mode) {
  if (_isSamePath(fromPath, toPath) && mode !== "into") return rootNodes;

  const [withoutNode, moved] = _removeAtPath(rootNodes, fromPath);
  if (!moved) return rootNodes;

  // Le retrait a pu décaler les index de `toPath` si `toPath` pointait après
  // `fromPath` dans la même fratrie — recalculer `toPath` sur `withoutNode`.
  const adjustedToPath = _adjustPathAfterRemoval(fromPath, toPath);

  if (mode === "into") {
    const parentNode = _getNode(withoutNode, adjustedToPath);
    if (!parentNode) return rootNodes;
    const renormalized = _renormalizeLevels(moved, parentNode.level);
    return _insertAtPath(withoutNode, adjustedToPath, (parentNode.children || []).length, renormalized);
  }

  // mode "before" / "after" : même fratrie que la cible.
  const parentPath = adjustedToPath.slice(0, -1);
  const targetIndex = adjustedToPath[adjustedToPath.length - 1];
  const insertIndex = mode === "after" ? targetIndex + 1 : targetIndex;
  // Niveau de la fratrie cible : dérivé du parent réel si imbriqué, sinon du
  // frère cible lui-même (`targetNode.level - 1`) — jamais une constante 0.
  // Un `0` en dur ignorait le niveau réel des autres nœuds racine (souvent 2,
  // pas 1) et rétrogradait silencieusement tout nœud réordonné à la racine,
  // sans jamais déclencher `validate_plan_tree` (arbre valide mais faux).
  const targetNode = _getNode(withoutNode, adjustedToPath);
  const parentLevel = parentPath.length
    ? _getNode(withoutNode, parentPath).level
    : (targetNode?.level ?? moved.level + 1) - 1;
  const renormalized = _renormalizeLevels(moved, parentLevel);
  return _insertAtPath(withoutNode, parentPath, insertIndex, renormalized);
}

function _isSamePath(a, b) {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

function _adjustPathAfterRemoval(fromPath, toPath) {
  // Même fratrie et toPath après fromPath → décaler d'un cran vers le bas.
  const sameParent =
    fromPath.length === toPath.length &&
    fromPath.slice(0, -1).every((v, i) => v === toPath[i]);
  if (sameParent && toPath[toPath.length - 1] > fromPath[fromPath.length - 1]) {
    const adjusted = [...toPath];
    adjusted[adjusted.length - 1] -= 1;
    return adjusted;
  }
  return toPath;
}
